import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, orderBy } from '../../firebase.config';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import GlobalExportButtons from '../GlobalExportButtons';
import { getShareDomain } from '../../utils/domainConfig';
import { buildReportFilename, exportTableAsImage } from './exportUtils';
import { printReportWithLetterhead, CollapsibleQuickTotals, ReportHeaderWithExport } from './CommonComponents';
import { useCompanyCurrency } from '../../hooks/useCompanyCurrency';
import { formatCurrencyIntl } from '../../utils/currencyUtils';

const AgingReport = ({ db, userId, appId, companyId, dateRange, selectedParty, parties, loading, setLoading }) => {
  const { currency: companyCurrency } = useCompanyCurrency();
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ totalOutstanding: 0 });
  const [showPartyBillsModal, setShowPartyBillsModal] = useState(false);
  const [modalParty, setModalParty] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);

  const parseDate = (input) => {
    if (!input) return null;
    if (input instanceof Date) return input;
    if (typeof input === 'object' && input.seconds) return new Date(input.seconds * 1000);
    const s = String(input).trim();
    let d = new Date(s.replace(/-/g, '/'));
    if (!isNaN(d.getTime())) return d;
    const f = new Date(s);
    return isNaN(f.getTime()) ? null : f;
  };

  const { sortedData, sortConfig, handleSort } = useTableSort(rows, { key: 'totalOutstanding', direction: 'desc' });
  const pagination = useTablePagination(sortedData, 25);

  // ESC key: step-by-step LIFO closing (invoice details -> party modal)
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (selectedInvoice) {
          setSelectedInvoice(null);
        } else if (showPartyBillsModal) {
          setShowPartyBillsModal(false);
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedInvoice, showPartyBillsModal]);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!db || !userId || !companyId) return;
      setLoading(true);
      try {
        // Fetch all sales invoices up to end date (as-on)
        const invSnap = await getDocs(query(getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills'), orderBy('invoiceDate', 'asc')));
        let invoices = invSnap.docs.map(doc => {
          const d = doc.data();
          return {
          id: doc.id,
            partyId: d.customFields?.party || d.party || d.partyId || d.customerId,
            date: d.invoiceDate || d.date,
            totalAmount: parseFloat(d.totalAmount || d.amount || 0),
            number: d.invoiceNumber || d.number || doc.id
          };
        }).filter(inv => {
          const d = parseDate(inv.date);
          return d && (!dateRange?.end || d <= parseDate(dateRange.end));
        });
        if (selectedParty) invoices = invoices.filter(i => i.partyId === selectedParty);

        // Fetch payments up to end date
        const paySnap = await getDocs(query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'), orderBy('paymentDate', 'asc')));
        const payments = paySnap.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            partyId: d.partyId || d.party,
            date: d.paymentDate || d.date,
            total: parseFloat(d.totalAmount || d.amount || 0),
            allocations: d.allocations || [],
            advanceAllocations: d.advanceAllocations || []
          };
        }).filter(p => {
          const d = parseDate(p.date);
          return d && (!dateRange?.end || d <= parseDate(dateRange.end));
        });

        // Build per-invoice paid using allocations only (no FIFO yet), also track last payment date per invoice
        const invoicePaidMap = new Map();
        const invoiceLastPayDate = new Map();
        payments.forEach(p => {
          const payDate = parseDate(p.date);
          (p.allocations || []).forEach(a => {
            if (a.billType === 'invoice' && a.billId) {
              const amt = parseFloat(a.allocatedAmount || 0);
              invoicePaidMap.set(a.billId, (invoicePaidMap.get(a.billId) || 0) + amt);
              const prev = invoiceLastPayDate.get(a.billId);
              if (!prev || (payDate && payDate > prev)) {
                invoiceLastPayDate.set(a.billId, payDate);
              }
            }
          });
        });

        // Aggregate per party
        const partyAgg = new Map();
        invoices.forEach(inv => {
          if (!inv.partyId) return;
          const paid = invoicePaidMap.get(inv.id) || 0;
          const due = Math.max(0, (inv.totalAmount || 0) - paid);
          const lastPay = invoiceLastPayDate.get(inv.id) || null;
          const invInfo = {
            id: inv.id,
            number: inv.number,
            date: parseDate(inv.date),
            total: inv.totalAmount || 0,
            paid,
            balance: due,
            lastPaymentDate: lastPay
          };
          const entry = partyAgg.get(inv.partyId) || {
            partyId: inv.partyId,
            totalOutstanding: 0,
            invoices: [],
            lastPaidDateParty: null
          };
          entry.invoices.push(invInfo);
          if (due > 0) entry.totalOutstanding += due;
          partyAgg.set(inv.partyId, entry);
        });

        // Add party opening balances to outstanding amounts
        parties.forEach(party => {
          if (party.openingBalance && party.openingBalance > 0) { // Only positive balances (receivable)
            const entry = partyAgg.get(party.id);
            if (entry) {
              entry.totalOutstanding += party.openingBalance;
            } else {
              // Create entry for party with only opening balance
              partyAgg.set(party.id, {
                partyId: party.id,
                totalOutstanding: party.openingBalance,
                invoices: [],
                lastPaidDateParty: null
              });
            }
          }
        });

        // Compute per-party derived metrics
        // Last paid date (any payment for party)
        const lastPaidDateByParty = new Map();
        payments.forEach(p => {
          const pid = p.partyId;
          const dt = parseDate(p.date);
          const prev = lastPaidDateByParty.get(pid);
          if (pid && dt && (!prev || dt > prev)) lastPaidDateByParty.set(pid, dt);
        });

        // Map to rows with names
        const rowsData = Array.from(partyAgg.values()).map(r => {
          const partyObj = (parties || []).find(p => p.id === r.partyId);
          // Outstanding invoices list
          const outstandingInvoices = (r.invoices || [])
            .filter(iv => iv.balance > 0)
            .sort((a, b) => (a.date || 0) - (b.date || 0));

          // Ageing metrics
          const lastPaidDateParty = lastPaidDateByParty.get(r.partyId) || null;
          const oldestOutstanding = outstandingInvoices[0] || null;
          const pendingRefDate = oldestOutstanding ? (oldestOutstanding.lastPaymentDate || oldestOutstanding.date) : null;

          return {
            partyId: r.partyId,
            partyName: partyObj?.firmName || partyObj?.name || r.partyId,
            totalOutstanding: r.totalOutstanding,
            outstandingCount: outstandingInvoices.length,
            outstandingInvoices,
            lastPaidDateParty,
            pendingRefDate
          };
        });

        const totalsCalc = rowsData.reduce((acc, r) => { acc.totalOutstanding += r.totalOutstanding; return acc; }, { totalOutstanding: 0 });
        setRows(rowsData);
        setTotals(totalsCalc);
      } catch (e) {
        console.error('Aging summary fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [db, userId, appId, dateRange, selectedParty, parties, setLoading]);

  const formatCurrency = (n) => new Intl.NumberFormat('en-IN', { 
    style: 'currency', 
          currency: companyCurrency, 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }).format(n || 0);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
  const diffDays = (from) => {
    if (!from) return '—';
    const today = new Date();
    const ms = today.setHours(0,0,0,0) - new Date(from).setHours(0,0,0,0);
    return Math.max(0, Math.floor(ms / (1000*60*60*24)));
  };

  // Summary metrics
  const totalOutstandingBills = rows.reduce((acc, r) => acc + (r.outstandingCount || 0), 0);
  const avgAgeFromLastPaid = (() => {
    const vals = rows.map(r => r.lastPaidDateParty).filter(Boolean).map(d => diffDays(d)).filter(v => typeof v === 'number');
    if (vals.length === 0) return '—';
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  })();
  const avgAgeFromOldestPending = (() => {
    const vals = rows.map(r => r.pendingRefDate).filter(Boolean).map(d => diffDays(d)).filter(v => typeof v === 'number');
    if (vals.length === 0) return '—';
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  })();

  // Export/Print/Share
  const exportCSV = () => {
    const headers = ['Party', 'Outstanding', 'Outstanding Bills', 'Age From Last Paid (days)', 'Age From Oldest Pending (days)'];
    const lines = sortedData.map(r => [
      r.partyName,
      r.totalOutstanding,
      r.outstandingCount || 0,
      typeof diffDays(r.lastPaidDateParty) === 'number' ? diffDays(r.lastPaidDateParty) : '',
      typeof diffDays(r.pendingRefDate) === 'number' ? diffDays(r.pendingRefDate) : ''
    ]);
    const csv = [headers.join(','), ...lines.map(l => l.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aging_summary_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Prepare export data for GlobalExportButtons
  const getExportData = () => sortedData.map(r => ({
    partyName: r.partyName,
    totalOutstanding: r.totalOutstanding,
    outstandingCount: r.outstandingCount || 0,
    ageFromLastPaid: typeof diffDays(r.lastPaidDateParty) === 'number' ? diffDays(r.lastPaidDateParty) : '-',
    ageFromOldestPending: typeof diffDays(r.pendingRefDate) === 'number' ? diffDays(r.pendingRefDate) : '-'
  }));

  const getExportColumns = () => [
    { key: 'partyName', label: 'Party' },
    { key: 'totalOutstanding', label: 'Outstanding' },
    { key: 'outstandingCount', label: '# Outstanding Bills' },
    { key: 'ageFromLastPaid', label: 'Age From Last Paid' },
    { key: 'ageFromOldestPending', label: 'Age From Oldest Pending' }
  ];

  const getReportDetails = () => ({
    'As on': new Date(dateRange.end).toLocaleDateString('en-IN'),
    'Total Outstanding': totals.totalOutstanding,
    'Total Bills': rows.reduce((a,b)=>a+(b.outstandingCount||0),0),
    dateRange
  });

  const printReport = () => {
    const columns = [
      { key: 'partyName', label: 'Party' },
      { key: 'totalOutstanding', label: 'Outstanding', 
        render: (value) => `Rs ${Number(value || 0).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}` },
      { key: 'outstandingCount', label: '# Outstanding Bills' },
      { key: 'lastPaidAge', label: 'Age From Last Paid',
        render: (value, row) => typeof diffDays(row.lastPaidDateParty) === 'number' ? diffDays(row.lastPaidDateParty) : '-' },
      { key: 'oldestPendingAge', label: 'Age From Oldest Pending',
        render: (value, row) => typeof diffDays(row.pendingRefDate) === 'number' ? diffDays(row.pendingRefDate) : '-' }
    ];

    const data = sortedData.map(r => ({
      partyName: r.partyName,
      totalOutstanding: r.totalOutstanding,
      outstandingCount: r.outstandingCount || 0,
      lastPaidDateParty: r.lastPaidDateParty,
      pendingRefDate: r.pendingRefDate
    }));

    const additionalInfo = {
      'As on': new Date(dateRange.end).toLocaleDateString('en-IN'),
      'Total Outstanding': `Rs ${Number(totals.totalOutstanding || 0).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`,
      'Total Bills': rows.reduce((a,b) => a + (b.outstandingCount || 0), 0)
    };

    printReportWithLetterhead('Aging Report (Summary)', data, columns, companyDetails, additionalInfo);
  };

  const exportImage = async () => {
    const filename = buildReportFilename({ prefix: 'AGING', dateRange });
    const columns = [
      { key: 'partyName', label: 'Party' },
      { key: 'totalOutstanding', label: 'Outstanding' },
      { key: 'outstandingCount', label: '# Outstanding Bills' },
      { key: 'lastPaidAge', label: 'Age From Last Paid' },
      { key: 'oldestPendingAge', label: 'Age From Oldest Pending' }
    ];
    
    const data = sortedData.map(r => ({
      partyName: r.partyName,
      totalOutstanding: r.totalOutstanding,
      outstandingCount: r.outstandingCount || 0,
      lastPaidAge: typeof diffDays(r.lastPaidDateParty) === 'number' ? diffDays(r.lastPaidDateParty) : '-',
      oldestPendingAge: typeof diffDays(r.pendingRefDate) === 'number' ? diffDays(r.pendingRefDate) : '-'
    }));
    
    await exportTableAsImage({
      data,
      columns,
      filename,
      companyDetails,
      reportDetails: {
        'As on': new Date(dateRange.end).toLocaleDateString('en-IN'),
        'Total Outstanding': formatCurrency(totals.totalOutstanding),
        'Total Bills': totalOutstandingBills,
        'Avg Age From Last Paid': typeof avgAgeFromLastPaid === 'number' ? `${avgAgeFromLastPaid} days` : '—',
        'Avg Age From Oldest Pending': typeof avgAgeFromOldestPending === 'number' ? `${avgAgeFromOldestPending} days` : '—'
      }
    });
  };

  const shareLink = () => {
    const shareData = {
      title: 'Aging Report (Summary)',
      text: `Total Outstanding: Rs ${Number(totals.totalOutstanding||0).toLocaleString('en-IN', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} | Bills: ${totalOutstandingBills}`,
      url: `${getShareDomain()}/reports/aging`
    };
    if (navigator.share) {
      navigator.share(shareData);
    } else {
      const url = `https://wa.me/?text=${encodeURIComponent(`${shareData.title}\n${shareData.text}\n${shareData.url}`)}`;
      window.open(url, '_blank');
    }
  };

  return (
    <div className="p-3 sm:p-6">
      <ReportHeaderWithExport
        title="Aging Report (Summary)"
        subtitle={`As on: ${new Date(dateRange.end).toLocaleDateString('en-IN')}`}
        exportButtons={
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="AGING"
            title="Aging Report (Summary)"
            companyDetails={companyDetails}
            reportDetails={getReportDetails()}
            disabled={rows.length === 0}
          />
        }
      />

      <CollapsibleQuickTotals
        cards={[
          { label: 'Total Outstanding', value: formatCurrency(totals.totalOutstanding), color: 'red' },
          { label: 'Outstanding Bills', value: totalOutstandingBills, color: 'blue' },
          { label: 'Avg Age (Last Paid)', value: typeof avgAgeFromLastPaid === 'number' ? `${avgAgeFromLastPaid} days` : '—', color: 'yellow' },
          { label: 'Avg Age (Oldest)', value: typeof avgAgeFromOldestPending === 'number' ? `${avgAgeFromOldestPending} days` : '—', color: 'green' }
        ]}
      />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="md:hidden p-3 space-y-2">
          {pagination.currentData.map((r) => (
            <div key={r.partyId} className="border border-gray-200 rounded-lg p-3 bg-white">
              <div className="font-medium text-gray-900 text-sm mb-2">{r.partyName}</div>
              <div className="flex justify-between items-center text-sm mb-2">
                <span className="text-red-600 font-semibold">{formatCurrency(r.totalOutstanding)}</span>
                <button
                  type="button"
                  className="text-blue-600 underline text-sm min-h-[44px] px-2 touch-manipulation"
                  onClick={() => { setModalParty(r); setShowPartyBillsModal(true); setSelectedInvoice(null); }}
                  disabled={(r.outstandingCount || 0) === 0}
                >
                  {r.outstandingCount || 0} bills
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                <span>Last paid: {diffDays(r.lastPaidDateParty)}</span>
                <span>Oldest pending: {diffDays(r.pendingRefDate)}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader columnKey="partyName" label="Party" onSort={handleSort} sortConfig={sortConfig} />
                <SortableHeader columnKey="totalOutstanding" label="Outstanding" onSort={handleSort} sortConfig={sortConfig} />
                <SortableHeader columnKey="outstandingCount" label="# Outstanding Bills" onSort={handleSort} sortConfig={sortConfig} />
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Age: From Last Paid</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Age: From Oldest Pending</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagination.currentData.map((r) => (
                <tr key={r.partyId}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-blue-600">{r.partyName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-red-600 font-semibold">{formatCurrency(r.totalOutstanding)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                    <button
                      className="text-blue-600 underline hover:text-blue-800"
                      onClick={() => { setModalParty(r); setShowPartyBillsModal(true); setSelectedInvoice(null); }}
                      disabled={(r.outstandingCount || 0) === 0}
                    >
                      {r.outstandingCount || 0}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{diffDays(r.lastPaidDateParty)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-center">{diffDays(r.pendingRefDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationControls {...pagination} />
      </div>

      {rows.length === 0 && !loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">No data</div>
          <p className="text-gray-400">No outstanding invoices found as on the selected date.</p>
        </div>
      )}

      {/* Party Bills Modal */}
      {showPartyBillsModal && modalParty && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-3xl relative max-h-[92dvh] overflow-y-auto">
            <button className="absolute top-2 right-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 text-xl touch-manipulation" onClick={() => setShowPartyBillsModal(false)} aria-label="Close">&times;</button>
            <h3 className="text-lg font-bold mb-4 pr-10">Outstanding Bills - {modalParty.partyName}</h3>
            <div className="md:hidden space-y-2 mb-4">
              {(modalParty.outstandingInvoices || []).map((iv) => (
                <button
                  key={iv.id}
                  type="button"
                  onClick={() => setSelectedInvoice(iv)}
                  className="w-full text-left border border-gray-200 rounded-lg p-3 touch-manipulation active:bg-blue-50"
                >
                  <div className="flex justify-between items-start gap-2 mb-1">
                    <span className="text-sm font-medium text-blue-600">{iv.number || iv.id}</span>
                    <span className="text-xs text-gray-500">{formatDate(iv.date)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Bal: <span className="text-red-600 font-semibold">{formatCurrency(iv.balance)}</span></span>
                    <span className="text-gray-600">{formatCurrency(iv.total)}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Bill No</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Paid</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Last Payment</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(modalParty.outstandingInvoices || []).map((iv) => (
                    <tr key={iv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-center text-blue-600 underline cursor-pointer" onClick={() => setSelectedInvoice(iv)}>
                        {iv.number || iv.id}
                      </td>
                      <td className="px-4 py-2 text-center">{formatDate(iv.date)}</td>
                      <td className="px-4 py-2 text-center">{formatCurrency(iv.total)}</td>
                      <td className="px-4 py-2 text-center">{formatCurrency(iv.paid)}</td>
                      <td className="px-4 py-2 text-center text-red-600 font-semibold">{formatCurrency(iv.balance)}</td>
                      <td className="px-4 py-2 text-center">{formatDate(iv.lastPaymentDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selectedInvoice && (
              <div className="mt-4 p-3 bg-gray-50 rounded border">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold">Bill Details: {selectedInvoice.number}</h4>
                  <button className="text-sm text-gray-600" onClick={() => setSelectedInvoice(null)}>Close</button>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                  <div><strong>Date:</strong> {formatDate(selectedInvoice.date)}</div>
                  <div><strong>Total:</strong> {formatCurrency(selectedInvoice.total)}</div>
                  <div><strong>Paid:</strong> {formatCurrency(selectedInvoice.paid)}</div>
                  <div><strong>Balance:</strong> {formatCurrency(selectedInvoice.balance)}</div>
                  <div><strong>Last Payment:</strong> {formatDate(selectedInvoice.lastPaymentDate)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgingReport; 